import { useDispatch, useSelector } from "react-redux";
import { ROUTES } from "routing/routes";

import {
  Box,
  ContextMenuButton,
  ContextMenuLink,
  Divider,
  Icon,
  Stack,
  Text
} from "@seneca/senekit";

import { linkToRegionModal } from "common/links";

import { isUserManaged } from "seneca-common/features/user/state";
import { openWindowSecurely } from "seneca-common/utils/routing/index";

import useUpdateApp from "features/app/hooks/useUpdateApp";
import { RocketEmoji } from "features/common/emoji";
import DarkModeMenuButton from "features/dark-mode/components/DarkModeMenuButton";
import { linkToAddCoursesWithQueryParams } from "features/dashboard/pages/courses-page/links";
import { NavbarSideDrawer } from "features/navbar";
import { useIsPlurallApp } from "features/plurall";
import { getLinkToPlurallStore } from "features/plurall/links";
import {
  getRegion,
  isSchoolAnalyticsEnabled
} from "features/regions/selectors";
import {
  linkToClassesPage,
  linkToInviteParentsPage,
  linkToResourceDiscoverTeacher,
  linkToResourcesHub,
  linkToSchoolPage,
  linkToTeacherHelpSection,
  linkToTeacherHomepage
} from "features/teachers/links";
import { linkToTeacherFeedback } from "features/teachers/routes/links";
import { setPlurallHelpCentreModalOpen } from "features/ui-state/actions";

import {
  linkToProgress,
  linkToTeacherWhatIsPremiumPage
} from "../../../../routes/links";
import useGetTeacherNavbarProps from "../../hooks/useGetTeacherNavbarProps";

export default function TeacherNavbarMobile() {
  const {
    email,
    locale,
    signOutOnClick,
    showInviteParents,
    sellPremiumToUser
  } = useGetTeacherNavbarProps();
  const {
    update: updateAppVersion,
    ready: appVersionUpdateReady,
    updating: appVersionIsUpdating
  } = useUpdateApp();

  const dispatch = useDispatch();

  const isPlurallApp = useIsPlurallApp();
  const userIsManaged = useSelector(isUserManaged);

  const schoolAnalyticsEnabled = useSelector(isSchoolAnalyticsEnabled);
  const region = useSelector(getRegion);

  return (
    <NavbarSideDrawer>
      {{
        options: (closeDrawer: any) => (
          <Stack space="large">
            {!isPlurallApp && (
              <>
                <Box margin="large">
                  <Text dataNoTranslate size="small" tone="neutral" truncate>
                    {email}
                  </Text>
                </Box>

                <Divider direction="horizontal" />
              </>
            )}

            <Stack>
              <ContextMenuLink
                onClick={closeDrawer}
                to={linkToTeacherHomepage()}
              >
                <Text>Home</Text>
              </ContextMenuLink>
              <ContextMenuLink onClick={closeDrawer} to={linkToClassesPage()}>
                <Text>Classes</Text>
              </ContextMenuLink>
              {region === "GB" && (
                <ContextMenuLink onClick={closeDrawer} to={linkToSchoolPage()}>
                  <Text>School</Text>
                </ContextMenuLink>
              )}
              {schoolAnalyticsEnabled && (
                <ContextMenuLink onClick={closeDrawer} to={linkToProgress()}>
                  <Text>Reports</Text>
                </ContextMenuLink>
              )}
            </Stack>

            <Divider direction="horizontal" />

            <Stack>
              <ContextMenuLink
                onClick={closeDrawer}
                to={ROUTES.TEACHER.SETTINGS.PERSONAL_INFORMATION.buildPath({})}
              >
                {{
                  icon: (
                    <Icon
                      iconName="cog"
                      size="medium"
                      accessibilityLabel={null}
                      tone="neutral"
                    />
                  ),
                  text: <Text>Settings</Text>
                }}
              </ContextMenuLink>

              <ContextMenuLink
                onClick={closeDrawer}
                to={linkToAddCoursesWithQueryParams({
                  price: "Free"
                })}
              >
                {{
                  icon: (
                    <Icon
                      iconName="graduationCap"
                      size="medium"
                      accessibilityLabel={null}
                      tone="neutral"
                    />
                  ),
                  text: <Text>Student Platform</Text>
                }}
              </ContextMenuLink>

              <DarkModeMenuButton />

              {showInviteParents && (
                // @ts-ignore fix-in-senekit
                <ContextMenuLink
                  onClick={() => {
                    closeDrawer();
                    openWindowSecurely(linkToInviteParentsPage(locale));
                  }}
                >
                  {{
                    icon: (
                      <Icon
                        iconName="plus"
                        size="medium"
                        accessibilityLabel={null}
                        tone="neutral"
                      />
                    ),
                    text: <Text>Invite parents</Text>
                  }}
                </ContextMenuLink>
              )}

              {!isPlurallApp && !userIsManaged && (
                <ContextMenuLink onClick={closeDrawer} to={linkToRegionModal()}>
                  {{
                    icon: (
                      <Icon
                        iconName="globe"
                        size="medium"
                        accessibilityLabel={null}
                        tone="neutral"
                      />
                    ),
                    text: <Text>Region</Text>
                  }}
                </ContextMenuLink>
              )}

              {!isPlurallApp && (
                <ContextMenuLink
                  onClick={closeDrawer}
                  to={linkToTeacherFeedback()}
                >
                  {{
                    icon: (
                      <Icon
                        iconName="megaphone"
                        size="medium"
                        accessibilityLabel={null}
                        tone="neutral"
                      />
                    ),
                    text: <Text>Feedback</Text>
                  }}
                </ContextMenuLink>
              )}

              <ContextMenuButton
                onClick={() =>
                  isPlurallApp
                    ? dispatch(setPlurallHelpCentreModalOpen())
                    : openWindowSecurely(linkToTeacherHelpSection(locale))
                }
              >
                {{
                  icon: (
                    <Icon
                      iconName="help"
                      size="medium"
                      accessibilityLabel={null}
                      tone="neutral"
                    />
                  ),
                  text: <Text>Help centre</Text>
                }}
              </ContextMenuButton>

              {!isPlurallApp && (
                <ContextMenuLink
                  onClick={closeDrawer}
                  to={linkToResourcesHub()}
                >
                  {{
                    icon: (
                      <Icon
                        iconName="star"
                        size="medium"
                        accessibilityLabel={null}
                        tone="neutral"
                      />
                    ),
                    text: <Text>News & CPD</Text>
                  }}
                </ContextMenuLink>
              )}

              {!isPlurallApp && (
                <ContextMenuLink
                  onClick={closeDrawer}
                  to={linkToResourceDiscoverTeacher()}
                >
                  {{
                    icon: (
                      <Icon
                        iconName="search"
                        size="medium"
                        accessibilityLabel={null}
                        tone="neutral"
                      />
                    ),
                    text: <Text>Resource search</Text>
                  }}
                </ContextMenuLink>
              )}

              {sellPremiumToUser && !isPlurallApp && (
                <ContextMenuLink
                  onClick={closeDrawer}
                  to={linkToTeacherWhatIsPremiumPage()}
                >
                  {{
                    icon: <RocketEmoji />,
                    text: <Text>What's premium?</Text>
                  }}
                </ContextMenuLink>
              )}

              {sellPremiumToUser && isPlurallApp && (
                <ContextMenuButton
                  onClick={() => openWindowSecurely(getLinkToPlurallStore())}
                >
                  {{
                    icon: <RocketEmoji />,
                    text: <Text>What's premium?</Text>
                  }}
                </ContextMenuButton>
              )}
            </Stack>

            <Divider direction="horizontal" />

            {appVersionUpdateReady && (
              <>
                <ContextMenuButton onClick={updateAppVersion}>
                  {{
                    icon: (
                      <Icon
                        iconName="reassign"
                        size="medium"
                        accessibilityLabel={null}
                        tone="neutral"
                      />
                    ),
                    text: (
                      <Text>
                        {appVersionIsUpdating ? (
                          <>Updating...</>
                        ) : (
                          <>Update Seneca</>
                        )}
                      </Text>
                    )
                  }}
                </ContextMenuButton>

                <Divider direction="horizontal" />
              </>
            )}

            <ContextMenuButton onClick={signOutOnClick}>
              {{
                icon: (
                  <Icon
                    iconName="leave"
                    size="medium"
                    accessibilityLabel={null}
                    tone="neutral"
                  />
                ),
                text: <Text>Logout</Text>
              }}
            </ContextMenuButton>
          </Stack>
        )
      }}
    </NavbarSideDrawer>
  );
}
